import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gavel, FileWarning, ShieldCheck, Plus, CheckCircle2, FileStack, UserCheck } from "lucide-react";
import {
  ujmsAdjudicateHearing, ujmsCaseWarrants, ujmsCaseConditions, ujmsConditionTypes, ujmsCaseCustody, ujmsViolationForHearing,
  ujmsHearingCustodyDirection,
} from "../services/ujmsApi";
import { Field, inputCls, selectCls, btnPrimary, btnGhost, cls, Panel, Badge, thCls, tdCls, fmtDT } from "./ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DraftOrderDialog } from "./pages/CaseDialogs";
import { ResolveDialog } from "./casetabs/ProbationTab";
import { HearingDecisionDialog } from "./HearingDecisionDialog";

const RESULT_PRESETS = ["Hearing held", "Warrant quashed — defendant appeared", "Conditions continued", "Matter resolved"];

export function ConditionsTab({ caseId }) {
  const [conds, setConds] = useState(null);
  useEffect(() => {
    ujmsCaseConditions(caseId).then((r) => setConds(r.data)).catch(() => setConds([]));
  }, [caseId]);
  if (conds === null) return <div className="text-sm text-slate-400 p-4">Loading conditions…</div>;
  const sorted = [...conds].sort((a, b) => (a.status === "ACTIVE" ? -1 : 1) - (b.status === "ACTIVE" ? -1 : 1));
  const active = conds.filter((c) => c.status === "ACTIVE").length;
  return (
    <Panel title={`Release Conditions — ${active} active, ${conds.length - active} past`} testId="tab-conditions">
      <div className="text-xs text-slate-500 px-1 pb-2">Conditions are reviewed and adjudicated at every hearing (Continue / Modify / Revoke) via the hearing's Adjudicate dialog.</div>
      <table className="w-full border-collapse">
        <thead><tr>
          <th className={thCls}>Condition</th><th className={thCls}>Terms</th><th className={thCls}>Status</th>
          <th className={thCls}>Imposed</th><th className={thCls}>Reviews</th><th className={thCls}>Ended</th>
        </tr></thead>
        <tbody>
          {sorted.map((c) => {
            const last = (c.reviews || [])[c.reviews?.length - 1];
            return (
              <tr key={c.id} className={cls("align-top", c.status === "ACTIVE" ? "hover:bg-slate-50" : "opacity-60 hover:bg-slate-50")} data-testid={`condition-row-${c.id}`}>
                <td className={`${tdCls} font-medium`}>{c.condition_label}</td>
                <td className={`${tdCls} text-sm text-slate-600`}>{c.details || "—"}</td>
                <td className={tdCls}><Badge value={c.status} /></td>
                <td className={`${tdCls} text-xs`}>{c.imposed_by || "—"}<div className="font-mono text-slate-400">{fmtDT(c.created_at)}</div></td>
                <td className={`${tdCls} text-xs`}>
                  {(c.reviews || []).length ? (
                    <>
                      <span className="font-semibold">{c.reviews.length}</span> review(s)
                      <div className="text-slate-400">last: {last.decision} by {last.reviewed_by} {fmtDT(last.created_at)}</div>
                    </>
                  ) : "—"}
                </td>
                <td className={`${tdCls} text-xs`}>
                  {c.terminated_at ? (<><span className="font-mono">{fmtDT(c.terminated_at)}</span><div className="text-slate-400">{c.terminate_reason || ""}</div></>) : "—"}
                </td>
              </tr>
            );
          })}
          {!sorted.length && <tr><td colSpan={6} className={`${tdCls} text-slate-400`}>No release conditions have been imposed on this case.</td></tr>}
        </tbody>
      </table>
    </Panel>
  );
}

export function ManageHearingDialog(props) {
  const [advanced, setAdvanced] = useState(false);
  useEffect(() => { if (props.open) setAdvanced(false); }, [props.open, props.hearing?.id]);
  return advanced ? <AdvancedHearingDialog {...props} /> : <HearingDecisionDialog {...props} onAdvanced={() => setAdvanced(true)} />;
}

function AdvancedHearingDialog({ open, onClose, hearing, onDone }) {
  const [warrants, setWarrants] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [types, setTypes] = useState([]);
  const [result, setResult] = useState("");
  const [notes, setNotes] = useState("");
  const [warrantAction, setWarrantAction] = useState("NONE");
  const [warrantReason, setWarrantReason] = useState("");
  const [appearance, setAppearance] = useState("");
  const [issueWarrant, setIssueWarrant] = useState("NONE");
  const [issueReason, setIssueReason] = useState("");
  const [issueBond, setIssueBond] = useState("");
  const [custody, setCustody] = useState(null);
  const [reviews, setReviews] = useState({});
  const [newConds, setNewConds] = useState([]);
  const [addType, setAddType] = useState("");
  const [addDetails, setAddDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [vopViolation, setVopViolation] = useState(null);
  const [vopResolving, setVopResolving] = useState(null);
  const [custodyDirection, setCustodyDirection] = useState("NONE");
  const [custodyReason, setCustodyReason] = useState("");
  const [directionReference, setDirectionReference] = useState("");
  const [authorizeRelease, setAuthorizeRelease] = useState(false);

  useEffect(() => {
    if (!open || !hearing) return;
    setResult(""); setNotes(""); setWarrantAction("NONE"); setWarrantReason(""); setReviews({}); setNewConds([]);
    setOffer(null); setOrderOpen(false); setVopViolation(null); setVopResolving(null);
    setAppearance(""); setIssueWarrant("NONE"); setIssueReason(""); setIssueBond(""); setCustody(null);
    setCustodyDirection("NONE"); setCustodyReason(""); setDirectionReference(""); setAuthorizeRelease(false);
    if (hearing.hearing_type === "REVOCATION") {
      ujmsViolationForHearing(hearing.id).then((r) => setVopViolation(r.data)).catch(() => setVopViolation(null));
    }
    ujmsCaseCustody(hearing.case_id).then((r) => setCustody(r.data)).catch(() => setCustody(null));
    ujmsCaseWarrants(hearing.case_id).then((r) =>
      setWarrants(r.data.filter((w) => ["REQUESTED", "ISSUED"].includes(w.status)))).catch(() => {});
    ujmsCaseConditions(hearing.case_id).then((r) => {
      const active = r.data.filter((c) => c.status === "ACTIVE");
      setConditions(active);
      setReviews(Object.fromEntries(active.map((c) => [c.id, { decision: "CONTINUED", details: "" }])));
    }).catch(() => {});
    ujmsConditionTypes().then((r) => { setTypes(r.data); setAddType(r.data[0]?.code || ""); }).catch(() => {});
  }, [open, hearing]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ujmsAdjudicateHearing(hearing.id, {
        result, notes,
        warrant_action: warrantAction === "NONE" ? null : warrantAction,
        warrant_reason: warrantReason,
        appearance: appearance || null,
        issue_warrant: issueWarrant === "NONE" ? null : issueWarrant,
        issue_warrant_reason: issueReason,
        issue_warrant_bond: issueBond ? Number(issueBond) : null,
        condition_reviews: conditions.map((c) => ({ condition_id: c.id, ...reviews[c.id] })),
        new_conditions: newConds.map(({ _k, ...n }) => n),
      });
      const s = r.data;
      toast.success(`HEARING_ADJUDICATED — ${s.result}`);
      if (s.warrants_recalled) toast.success(`${s.warrants_recalled} warrant(s) recalled — active warning removed`);
      if (s.warrant_issued) {
        toast.success(`${s.warrant_issued.type} warrant ${s.warrant_issued.status} — docketed`);
        if (s.warrant_issued.return_hearing) toast.success(`Warrant return hearing auto-set for ${s.warrant_issued.return_hearing.date} at 09:00`);
      }
      if (s.remanded_to_custody) toast.success("Remand direction sent to the Sheriff booking queue");
      if (s.custody_released) toast.success("Release direction sent to the Sheriff workflow");
      if (s.custody_release_blocked) toast.error(`Custody release blocked: ${s.custody_release_blocked}`);
      if (s.conditions_revoked || s.conditions_modified || s.conditions_imposed)
        toast.success(`Conditions: ${s.conditions_continued} continued, ${s.conditions_modified} modified, ${s.conditions_revoked} revoked, ${s.conditions_imposed} imposed`);
      const query = (s.warrants_recalled || s.warrant_issued) ? "warrant" : (s.conditions_revoked || s.conditions_modified || s.conditions_imposed) ? "conditions" : "";
      setOffer({ summary: s, query });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Adjudication failed"); }
    setBusy(false);
  };
  const recordCustodyDirection = async () => {
    setBusy(true);
    try {
      const { data } = await ujmsHearingCustodyDirection(hearing.id, { direction: custodyDirection, reason: custodyReason, entered_direction_reference: directionReference, authorize_release: authorizeRelease });
      toast.success(data.direction === "REMAND" ? "Remand ordered — Sheriff booking queue created" : data.status === "RELEASE_AUTHORIZED" ? "Release authorized — Sheriff execution queue created" : "Release direction sent for court review");
      setCustody(data.direction === "RELEASE" ? { ...custody, in_custody: true } : custody); setCustodyDirection("NONE"); setCustodyReason(""); setDirectionReference(""); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Court custody direction failed"); }
    setBusy(false);
  };

  if (!hearing) return null;
  if (offer) {
    return (
      <>
        <Dialog open={open && !orderOpen} onOpenChange={(v) => !v && onClose()}>
          <DialogContent className="max-w-lg" data-testid="mh-offer-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" /> Hearing adjudicated
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-600 space-y-1">
              <div>“{offer.summary.result}”</div>
              {offer.summary.appearance && <div>• Appearance recorded: {offer.summary.appearance.replace(/_/g, " ").toLowerCase()}</div>}
              {offer.summary.warrant_issued && <div className="text-red-700 font-medium">• {offer.summary.warrant_issued.type} warrant {offer.summary.warrant_issued.status} — docketed{offer.summary.warrant_issued.return_hearing ? `; return hearing ${offer.summary.warrant_issued.return_hearing.date}` : ""}</div>}
              {offer.summary.remanded_to_custody && <div className="text-red-700 font-medium">• Remand direction sent — Sheriff must confirm physical booking</div>}
              {offer.summary.custody_released && <div className="text-emerald-700 font-medium">• Release direction sent — Sheriff executes after authorization and hold checks</div>}
              {offer.summary.custody_release_blocked && <div className="text-amber-700 font-medium">• Custody release BLOCKED: {offer.summary.custody_release_blocked}</div>}
              {offer.summary.warrants_recalled > 0 && <div>• {offer.summary.warrants_recalled} warrant(s) recalled — warning banner removed</div>}
              {(offer.summary.conditions_continued + offer.summary.conditions_modified + offer.summary.conditions_revoked) > 0 &&
                <div>• Conditions reviewed: {offer.summary.conditions_continued} continued, {offer.summary.conditions_modified} modified, {offer.summary.conditions_revoked} revoked</div>}
              {offer.summary.conditions_imposed > 0 && <div>• {offer.summary.conditions_imposed} new condition(s) imposed</div>}
              <div className="pt-2 text-slate-700 font-medium">Draft the matching order now? Library templates auto-fill from the case record and this hearing.</div>
            </div>
            <DialogFooter>
              <button className={btnGhost} onClick={onClose} data-testid="mh-offer-skip">Done — no order</button>
              <button className={btnPrimary} onClick={() => setOrderOpen(true)} data-testid="mh-offer-order-btn">
                <FileStack className="w-4 h-4" /> Draft Matching Order
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <DraftOrderDialog open={orderOpen} onClose={() => { setOrderOpen(false); onClose(); }} caseId={hearing.case_id} onDone={onDone} />
      </>
    );
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manage-hearing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gavel className="w-4 h-4" />
            Adjudicate {hearing.hearing_type_label || hearing.hearing_type} — {hearing.case_number || ""}
          </DialogTitle>
        </DialogHeader>

        {hearing.hearing_type === "REVOCATION" && vopViolation && (
          <div className="border border-red-300 bg-red-50 rounded-sm p-3 flex items-start justify-between gap-3" data-testid="mh-vop-banner">
            <div className="text-[12.5px]">
              <div className="font-semibold text-red-800">Petition for Violation of Probation pending</div>
              <div className="text-slate-700">{(vopViolation.category || "").replace(/_/g, " ")} ({vopViolation.severity}) — {vopViolation.description}</div>
              {vopViolation.recommendation && <div className="text-slate-500 text-[11px] mt-0.5">PO recommendation: {vopViolation.recommendation}</div>}
            </div>
            <button className={btnPrimary} onClick={() => setVopResolving({ v: vopViolation, term: { conditions: vopViolation.term_conditions || [] } })} data-testid="mh-rule-vop-btn"><Gavel className="w-3.5 h-3.5" /> Rule on VOP</button>
          </div>
        )}

        <Field label="Hearing result (docketed)" required>
          <div className="space-y-1.5">
            <input className={inputCls} value={result} onChange={(e) => setResult(e.target.value)}
              placeholder="e.g. Warrant quashed — defendant appeared and was admonished" data-testid="mh-result-input" />
            <div className="flex flex-wrap gap-1.5">
              {RESULT_PRESETS.map((p) => (
                <button key={p} type="button" onClick={() => setResult(p)}
                  className="text-[11px] border border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-sm px-2 py-0.5 text-slate-600"
                  data-testid={`mh-preset-${p.split(" ")[0].toLowerCase()}`}>{p}</button>
              ))}
            </div>
          </div>
        </Field>

        {/* Defendant appearance & custody */}
        <div className="border border-[#c8d4e0] bg-ujms-soft rounded-sm p-3 space-y-2" data-testid="mh-appearance-section">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 flex-wrap">
            <UserCheck className="w-4 h-4 text-ujms" /> Defendant appearance
            {custody?.in_custody && (
              <span className="text-[10px] font-bold uppercase bg-red-700 text-white rounded-sm px-2 py-0.5" data-testid="mh-custody-chip">
                Currently in custody{custody.record?.facility ? ` — ${custody.record.facility}` : ""}
              </span>
            )}
            {custody && !custody.in_custody && (
              <span className="text-[10px] font-bold uppercase bg-slate-200 text-slate-600 rounded-sm px-2 py-0.5" data-testid="mh-custody-chip">
                Not in custody
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {[["PRESENT_IN_COURT", "Present in court"],
              ["PRESENT_IN_CUSTODY", "Present — in custody"],
              ["RELEASED_PENDING_TRIAL", "Released pending trial"],
              ["FAILED_TO_APPEAR", "Failed to appear"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="mh-appearance" checked={appearance === v}
                  onChange={() => {
                    setAppearance(v);
                  }}
                  data-testid={`mh-appearance-${v.toLowerCase().replace(/_/g, "-")}`} /> {l}
              </label>
            ))}
            {appearance && (
              <button type="button" className="text-[11px] text-slate-400 underline" onClick={() => setAppearance("")} data-testid="mh-appearance-clear">clear</button>
            )}
          </div>
          {appearance === "PRESENT_IN_CUSTODY" && custody && !custody.in_custody && (
            <div className="text-[11px] text-red-700 font-medium">Record a Remand to Sheriff direction below; Sheriff confirms the physical booking separately.</div>
          )}
          {appearance === "RELEASED_PENDING_TRIAL" && custody?.in_custody && (
            <div className="text-[11px] text-emerald-700 font-medium">Record a Release direction below; Sheriff execution follows court authorization and hold checks.</div>
          )}
          {appearance === "FAILED_TO_APPEAR" && (
            <div className="text-[11px] text-amber-800 font-medium">Failure to appear does not automatically select or issue a warrant. Record the judge's decision explicitly.</div>
          )}
        </div>

        {/* Issue new warrant */}
        <div className={cls("border rounded-sm p-3 space-y-2", issueWarrant !== "NONE" ? "border-red-300 bg-red-50" : "border-slate-200")} data-testid="mh-issue-warrant-section">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <FileWarning className={cls("w-4 h-4", issueWarrant !== "NONE" ? "text-red-600" : "text-slate-400")} /> Enter warrant at this hearing
          </div>
          <div className="flex flex-wrap gap-3">
            {[["NONE", "No new warrant"], ["BENCH", "Bench warrant"], ["ARREST", "State (arrest) warrant"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="mh-issue-warrant" checked={issueWarrant === v} onChange={() => setIssueWarrant(v)}
                  data-testid={`mh-issue-warrant-${v.toLowerCase()}`} /> {l}
              </label>
            ))}
          </div>
          {issueWarrant !== "NONE" && (
            <div className="flex flex-wrap gap-2">
              <input className={`${inputCls} flex-1 min-w-[220px]`} value={issueReason} onChange={(e) => setIssueReason(e.target.value)}
                placeholder="Reason (docketed with the warrant)" data-testid="mh-issue-warrant-reason" />
              <input className={`${inputCls} w-36`} type="number" min="0" value={issueBond} onChange={(e) => setIssueBond(e.target.value)}
                placeholder="Bond $ (opt.)" data-testid="mh-issue-warrant-bond" />
            </div>
          )}
          {issueWarrant !== "NONE" && (
            <div className="text-[11px] text-red-600">The {issueWarrant === "BENCH" ? "bench" : "state arrest"} warrant is issued immediately on adjudication (judges) or queued for judicial review, docketed, and raises the case's active-warrant banner.</div>
          )}
        </div>

        {warrants.length > 0 && (
          <div className="border border-red-200 bg-red-50 rounded-sm p-3 space-y-2" data-testid="mh-warrant-section">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
              <FileWarning className="w-4 h-4" /> Active warrant{warrants.length > 1 ? "s" : ""} on this case
            </div>
            {warrants.map((w) => (
              <div key={w.id} className="text-xs text-red-700 font-mono">
                {w.warrant_type} — {w.person_name || "subject"} · {w.status} · issued {(w.issued_at || w.created_at || "").slice(0, 10)}
              </div>
            ))}
            <div className="flex flex-wrap gap-3 pt-1">
              {[["NONE", "No action — remains active"], ["QUASH", "Quash & recall warrant(s)"], ["RECALL", "Recall warrant(s)"]].map(([v, l]) => (
                <label key={v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name="mh-warrant" checked={warrantAction === v} onChange={() => setWarrantAction(v)}
                    data-testid={`mh-warrant-${v.toLowerCase()}`} /> {l}
                </label>
              ))}
            </div>
            {warrantAction !== "NONE" && (
              <input className={inputCls} value={warrantReason} onChange={(e) => setWarrantReason(e.target.value)}
                placeholder="Reason (defaults to 'Quashed in open court…')" data-testid="mh-warrant-reason" />
            )}
            {warrantAction !== "NONE" && (
              <div className="text-[11px] text-red-600">On adjudication the warrant is recalled, docketed, and the active-warrant warning banner is removed automatically.</div>
            )}
          </div>
        )}

        <div className="border border-slate-200 rounded-sm p-3 space-y-2" data-testid="mh-conditions-section">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <ShieldCheck className="w-4 h-4" /> Release conditions review
            <span className="text-[11px] font-normal text-slate-400">— conditions must be reviewed and adjudicated at every hearing</span>
          </div>
          {conditions.length === 0 && <div className="text-xs text-slate-400">No active release conditions on this case.</div>}
          {conditions.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-2" data-testid={`mh-cond-${c.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm font-medium">{c.condition_label}</div>
                {c.details && <div className="text-xs text-slate-500">{c.details}</div>}
              </div>
              <select className={`${selectCls} w-36`} value={reviews[c.id]?.decision || "CONTINUED"}
                onChange={(e) => setReviews((r) => ({ ...r, [c.id]: { ...r[c.id], decision: e.target.value } }))}
                data-testid={`mh-cond-decision-${c.id}`}>
                <option value="CONTINUED">Continue</option>
                <option value="MODIFIED">Modify</option>
                <option value="REVOKED">Revoke</option>
              </select>
              {["MODIFIED", "REVOKED"].includes(reviews[c.id]?.decision) && (
                <input className={`${inputCls} w-64`} value={reviews[c.id]?.details || ""}
                  onChange={(e) => setReviews((r) => ({ ...r, [c.id]: { ...r[c.id], details: e.target.value } }))}
                  placeholder={reviews[c.id]?.decision === "MODIFIED" ? "New terms…" : "Revocation reason…"}
                  data-testid={`mh-cond-details-${c.id}`} />
              )}
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <select className={`${selectCls} w-56`} value={addType} onChange={(e) => setAddType(e.target.value)} data-testid="mh-add-cond-type">
              {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
            <input className={`${inputCls} flex-1 min-w-[160px]`} value={addDetails} onChange={(e) => setAddDetails(e.target.value)}
              placeholder="Terms (optional)" data-testid="mh-add-cond-details" />
            <button type="button" className={btnGhost} disabled={!addType}
              onClick={() => { setNewConds((n) => [...n, { _k: Math.random().toString(36).slice(2), condition_type: addType, details: addDetails }]); setAddDetails(""); }}
              data-testid="mh-add-cond-btn"><Plus className="w-3.5 h-3.5" /> Impose Condition</button>
          </div>
          {newConds.map((n, i) => (
            <div key={n._k || i} className="text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-sm px-2 py-1 inline-flex items-center gap-2 mr-1.5" data-testid={`mh-new-cond-${i}`}>
              {types.find((t) => t.code === n.condition_type)?.label || n.condition_type}{n.details ? ` — ${n.details}` : ""}
              <button className="text-red-600 font-bold" onClick={() => setNewConds((x) => x.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
        </div>

        <div className="border border-red-200 bg-red-50/40 rounded-sm p-3 space-y-2" data-testid="mh-custody-direction-section">
          <div className="text-sm font-semibold text-slate-800">Court custody direction</div>
          <div className="flex flex-wrap gap-3"><label className="flex gap-1.5 text-sm"><input type="radio" checked={custodyDirection === "NONE"} onChange={() => setCustodyDirection("NONE")} data-testid="mh-custody-direction-none" />No custody direction</label><label className="flex gap-1.5 text-sm"><input type="radio" checked={custodyDirection === "REMAND"} onChange={() => setCustodyDirection("REMAND")} data-testid="mh-custody-direction-remand" />Remand to Sheriff</label><label className="flex gap-1.5 text-sm"><input type="radio" checked={custodyDirection === "RELEASE"} onChange={() => setCustodyDirection("RELEASE")} data-testid="mh-custody-direction-release" />Release direction</label></div>
          {custodyDirection !== "NONE" && <><div className="grid sm:grid-cols-2 gap-2"><input className={inputCls} value={directionReference} onChange={(e) => setDirectionReference(e.target.value)} placeholder="Entered judge direction / order reference" data-testid="mh-custody-direction-reference" /><input className={inputCls} value={custodyReason} onChange={(e) => setCustodyReason(e.target.value)} placeholder="Reason for the record" data-testid="mh-custody-direction-reason" /></div>{custodyDirection === "RELEASE" && <label className="flex gap-1.5 text-xs" data-testid="mh-release-authorize-label"><input type="checkbox" checked={authorizeRelease} onChange={(e) => setAuthorizeRelease(e.target.checked)} data-testid="mh-release-authorize-checkbox" />Entered direction authorizes Sheriff release execution after hold checks</label>}<button type="button" className={btnPrimary} disabled={busy || directionReference.trim().length < 3 || custodyReason.trim().length < 3} onClick={recordCustodyDirection} data-testid="mh-record-custody-direction">Record & send to Sheriff</button></>}
        </div>

        <Field label="Notes (appended to hearing record)">
          <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mh-notes" />
        </Field>

        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={cls(btnPrimary)} disabled={!result.trim() || busy} onClick={submit} data-testid="mh-submit-btn">
            <Gavel className="w-4 h-4" /> {busy ? "Adjudicating…" : "Adjudicate & Complete Hearing"}
          </button>
        </DialogFooter>
      </DialogContent>
      <ResolveDialog resolving={vopResolving} caseId={hearing.case_id} catalog={null}
        onClose={() => setVopResolving(null)}
        onDone={() => { setVopResolving(null); setVopViolation(null); onDone && onDone(); onClose(); }} />
    </Dialog>
  );
}
